import axios from 'axios';
import type { TemplateData } from '@/lib/template';

const BASE_URL = `${process.env.VUE_APP_API_ROOT}/organizations`;
const getUrl = (orgSlug: string, prjSlug: string, slug = '') => `${BASE_URL}/${orgSlug}/projects/${prjSlug}/templates/${slug}`;

interface AllResponse {
  data: TemplateData[];
  success: boolean;
}

interface GetResponse {
  data: TemplateData;
  success: boolean;
}

interface PostResponse {
  data: { slug: string };
  success: boolean;
  message: string;
}

interface PutResponse {
  data: {
    name: string;
    slug: string;
  };
  success: boolean;
  message: string;
}

interface DeleteResponse {
  success: boolean;
}

export const templateService = {
  async getAll(orgSlug: string, prjSlug: string): Promise<TemplateData[]> {
    const res = await axios.get<AllResponse>(`${BASE_URL}/${orgSlug}/projects/${prjSlug}/templates`);
    return res.data.data;
  },

  async getOne(orgSlug: string, prjSlug: string, slug: string): Promise<TemplateData> {
    const res = await axios.get<GetResponse>(getUrl(orgSlug, prjSlug, slug));
    return res.data.data;
  },

  async add(orgSlug: string, prjSlug: string, data: TemplateData): Promise<{ slug: string }> {
    const res = await axios.post<TemplateData, { data: PostResponse & { errors?: any } }>(getUrl(orgSlug, prjSlug), data, {
      validateStatus: () => true,
    });

    if (!res.data.success) {
      const error = new Error(res.data.message) as Error & {
        validationErrors?: any[];
        totalAnnotations?: number;
        totalErrors?: number;
      };

      if (res.data.errors) {
        error.validationErrors = res.data.errors.errors || [];
        error.totalAnnotations = res.data.errors.totalAnnotations || 0;
        error.totalErrors = res.data.errors.totalErrors || 0;
      }

      throw error;
    }

    return res.data.data;
  },

  async update(orgSlug: string, prjSlug: string, slug: string, data: TemplateData): Promise<{ slug: string }> {
    const res = await axios.put<TemplateData, { data: PutResponse & { errors?: any } }>(getUrl(orgSlug, prjSlug, slug), data, { validateStatus: () => true });
    if (!res.data.success) {
      const error = new Error(res.data.message) as Error & {
        validationErrors?: any[];
        totalAnnotations?: number;
        totalErrors?: number;
      };

      if (res.data.errors) {
        error.validationErrors = res.data.errors.errors || [];
        error.totalAnnotations = res.data.errors.totalAnnotations || 0;
        error.totalErrors = res.data.errors.totalErrors || 0;
      }

      throw error;
    }

    return res.data.data;
  },

  async delete(orgSlug: string, prjSlug: string, slug: string): Promise<boolean> {
    const res = await axios.delete<DeleteResponse>(getUrl(orgSlug, prjSlug, slug));
    return res.data.success;
  },
};
